import type { InternalUrl } from '@tbitop/routing';
import { useLink, useRouting } from '@tbitop/routing';
import classNames from 'classnames';
import { isString, isUndefined } from 'lodash-es';
import { Fragment } from 'react';

type Link = {
  name: string;
  href: string | InternalUrl;
  disabled?: boolean;
};

type SegmentedControllerProps = {
  links: Link[];
};

export const SegmentedController = ({ links }: SegmentedControllerProps) => {
  const { isActiveRoute } = useRouting();

  return (
    (<nav className="flex flex-row" aria-label="App-Navigation">
      {links.map(({ href, name, disabled }, i) => {
        const active = isActiveRoute(href);
        const inactiveEnabled = !active && !disabled;
        const next = links[i + 1];
        const nextInactiveEnabled = !isUndefined(next) && !isActiveRoute(next.href) && !next.disabled;

        return (
          (<Fragment key={isString(href) ? href : href.pathname}>
            <LinkItem href={href} text={name} active={active} disabled={disabled} />
            {/*
            Programmatically insert separator between INACTIVE + ENABLED elements
            CSS :after is quite tricky in Tailwind when it comes to inserting JUST a vertical line without text content
            */}
            {inactiveEnabled && nextInactiveEnabled ? <LinkSeparator /> : null}
          </Fragment>)
        );
      })}
    </nav>)
  );
};

type LinkItemProps = {
  href: string | InternalUrl;
  text: string;
  active: boolean;
  disabled?: boolean;
};

const LinkItem = ({ href, text, active, disabled }: LinkItemProps) => {
  const Link = useLink();

  return (
    <Link
      tabIndex={disabled ? -1 : undefined }
      aria-disabled={disabled}
      href={href}
      aria-current={active ? 'page' : undefined}
      className={classNames('tds-segmented-controller-item py-150 px-300', {
        'selected': active,
        'disabled': disabled,
      })}
    >
      {text}
    </Link>
  );
};

const LinkSeparator = () => <div className="tds-segmented-controller-item-separator py-150"><span className="block h-full"/></div>;
